import { action, observable } from 'mobx';
import moment from 'moment';
import api from 'core/util/api';
import type DeviceModel from 'app/stores/device/DeviceModel';
import { Model } from 'core/model/Model';
import { DeviceTrapFields, DeviceTrapKeys } from '@kentik/ui-shared/device/constants';
import { SimpleModel } from 'core/model/model.types';
import { PagedCollection } from 'core/model/PagedCollection';
import { ApiResponseWithJsonRoot } from 'core/model/Collection';

type DeviceTrapsModel = SimpleModel<keyof typeof DeviceTrapFields>;

export interface DeviceTrapsFilters {
  trap_type?: string[];
}

/**
 * Removes irrelevant fields from filter object (filter fields which have no associated filter value)
 */
const cleanFilters = (filters: DeviceTrapsFilters = {}) =>
  Object.fromEntries(Object.entries(filters).filter(([, values]) => !!values?.length));

const TRAP_WINDOW_MS = 3600 * 1000;
const MINUTE_MS = 60 * 1000;

export class DeviceTrapsCollection extends PagedCollection<Model<DeviceTrapsModel>> {
  @observable.ref
  device: DeviceModel;

  @observable.ref
  filters: DeviceTrapsFilters = {};

  @observable
  searchTerm = '';

  /** start/end in milliseconds */
  @observable.ref
  timeRange: { startDate: number; endDate: number } = {
    startDate: moment().subtract(1, 'hours').seconds(0).unix() * 1000,
    endDate: moment().seconds(0).unix() * 1000
  };

  @observable
  queryReady = false;

  get model() {
    return Model<DeviceTrapsModel>;
  }

  constructor(device: DeviceModel) {
    super();
    this.device = device;
    this.serverSortableFields = [DeviceTrapKeys.timestamp, DeviceTrapKeys.trap_type];
    this.setPageState({ perPage: 20 });
    this.requestStatus = 'awaitingRequest';
  }

  get url() {
    return `/api/ui/devices/${this.device.id}/traps/query`;
  }

  get fetchMethod() {
    return 'post' as const;
  }

  protected get jsonRoot() {
    return 'data';
  }

  protected get queuedFetchKey() {
    return `DeviceTrapsCollection.${this.device.id}`;
  }

  protected get defaultSortState(): { field: typeof DeviceTrapKeys.timestamp; direction: 'desc' } {
    return { field: DeviceTrapKeys.timestamp, direction: 'desc' };
  }

  get sortParam(): { field?: string; direction?: 'asc' | 'desc' } {
    const { field, direction } = this.sortState;
    return { field, direction };
  }

  @action
  setFilters(filters: DeviceTrapsFilters) {
    this.filters = filters;
  }

  @action
  setSearchTerm(searchTerm: string) {
    this.searchTerm = searchTerm;
  }

  /**
   * @param timeRange start/end in milliseconds
   */
  @action
  setTimeRange(timeRange: { startDate: number; endDate: number }) {
    this.timeRange = timeRange;
  }

  /**
   * Moves the time range to the hour containing the device's most recent trap. Keeps the current range when
   * there is no trap or the lookup fails.
   * @returns whether the range was moved
   */
  async jumpToLatestTrap(): Promise<boolean> {
    try {
      const { lastSeen }: { lastSeen: number | null } = await api.get(
        `/api/ui/devices/${this.device.id}/traps/last-seen`
      );
      const latest = Number(lastSeen) * 1000;
      if (!Number.isFinite(latest) || latest <= 0) {
        return false;
      }
      // window end is exclusive, so round up to the next minute to include the trap itself
      const endDate = (Math.floor(latest / MINUTE_MS) + 1) * MINUTE_MS;
      this.setTimeRange({ startDate: endDate - TRAP_WINDOW_MS, endDate });
      return true;
    } catch {
      // keep the current range
      return false;
    }
  }

  /**
   * Enables querying. Fetches are skipped until the initial query state (e.g. saved search term) has been applied.
   */
  @action
  setQueryReady(queryReady: boolean) {
    this.queryReady = queryReady;
  }

  /**
   * Request body for the traps query, built from the collection's own filter, window, search, sort and page state.
   */
  get queryData() {
    const { field, direction } = this.sortParam;
    return {
      filters: cleanFilters(this.filters),
      window: { startDate: this.timeRange.startDate / 1000, endDate: this.timeRange.endDate / 1000 },
      pagination: { limit: this.pageState.perPage, offset: this.pageState.offset },
      search: this.searchTerm,
      sort: { field, direction }
    };
  }

  /**
   * Fetches the current page using the collection's query state via the queued `pagedFetch`.
   */
  fetchPage({ perPage }: { perPage?: number } = {}) {
    if (!this.queryReady) {
      return Promise.resolve();
    }
    if (perPage !== undefined) {
      this.setPageState({ perPage });
    }
    return this.pagedFetch({ data: this.queryData });
  }

  /**
   * Server sort (and any change to filters/search/window) returns to the first page and re-queries with the
   * current state, rather than the base implementation's un-queued `loadMoreItems`.
   */
  serverSortFn() {
    this.resetPageState();
    return this.fetchPage();
  }

  processData(response: ApiResponseWithJsonRoot<DeviceModel>) {
    const result = super.processData(response);
    this.setPageState({ pageCountLoaded: true });
    return result;
  }
}
